// Renewal data-loading engine (search-row + QR renewal entry points and
// the shared record-extraction / form-population helpers).
//
// Extracted verbatim from DealerForm.vue; merged back via `mixins`.
import { getAllMembershipInfo } from "@/services/api/membership";
import {
  coerceAccountField,
  coerceBoatField,
  coerceContactField,
} from "../loadedRecord";

export default {
  methods: {
    RenewMembership(row, index, detailsShowing) {
      if (detailsShowing) {
        this.detailsShowing = false;
        return;
      }

      row.toggleDetails();

      // Unbind the previous member first: this load can bail early (promo
      // conflict) or fail, and only populateRenewalForm rebinds.
      this.clearRecordIdentity();

      getAllMembershipInfo(this.response_data[index]["account__c"])
        .then(async (response) => {
          this.response_data[index]["full_data"] = response["data"];
          console.log(response["data"]);
          await this.loadStripeCustomerFromMemberInfo(response);
        })
        .then(() => {
          const full_data = this.response_data[index]["full_data"];
          const records = this.loadPrimaryRecords(full_data);
          const { primary_membership } = records;
          this.applyMembershipSelections(primary_membership);

          // Extract values and checks to improve readability
          const renewalPromoCode =
            primary_membership["renewal_promotion_code_name__c"] || "";
          const hasRenewalPromoCode = !!renewalPromoCode;
          const formPromoCode = this.promotion_code || "";
          const hasFormPromoCode = !!formPromoCode;

          // Case 1: Form has promo code but membership doesn't have renewal code
          if (!hasRenewalPromoCode && hasFormPromoCode) {
            console.log("Using promo code from form");
            this.submitPromo(formPromoCode);
          }

          // Case 2: Membership has renewal code but form doesn't have promo code
          if (hasRenewalPromoCode && !hasFormPromoCode) {
            console.log("Using renewal promo code from membership");
            this.promotion_code = renewalPromoCode;
            this.submitPromo(renewalPromoCode);
          }

          // Case 3: Both membership and form have promo codes
          if (hasRenewalPromoCode && hasFormPromoCode) {
            console.log("Both membership and form have promo codes");

            // If codes match, use it directly
            if (formPromoCode === renewalPromoCode) {
              this.submitPromo(formPromoCode);
            } else {
              // Codes don't match, prompt user for confirmation
              this.tempFormPromoCode = formPromoCode;
              this.renewalPromoCode = renewalPromoCode;
              this.showPromoCodeModal = true;
              return;
            }
          }

          // Implicit case: Neither has a promo code - continue with the form
          this.populateRenewalForm(full_data, records);
        })
        .catch((error) =>
          this.handleGetAllInfoError(error, "search-row-expand")
        );
    },
    async RenewMembershipViaAccountId(AccountId) {
      let index = 0;

      if (this.response_data.length === 0) {
        console.log("response_data is empty");
        this.response_data.push({
          account__c: AccountId,
          full_data: {},
        });
      } else {
        console.log("response_data is not empty");
      }

      this.clearRecordIdentity();

      getAllMembershipInfo(AccountId)
        .then(async (response) => {
          this.response_data[index]["full_data"] = response["data"];
          console.log(response["data"]);
          await this.loadStripeCustomerFromMemberInfo(response);
        })
        .then(() => {
          const full_data = this.response_data[index]["full_data"];
          const records = this.loadPrimaryRecords(full_data);
          const { primary_membership } = records;
          this.applyMembershipSelections(primary_membership);

          // With this:
          if (
            this.promotion_code != undefined &&
            this.promotion_code != null &&
            this.promotion_code != ""
          ) {
            this.handlePromotionCode(primary_membership);
          }

          this.populateRenewalForm(full_data, records);
        })
        .catch((error) => this.handleGetAllInfoError(error, "renewal-search"));
    },
    // Shared by RenewMembership / RenewMembershipViaAccountId: pull the
    // primary Stripe customer (if any) out of the member-info response and
    // reset any payment state belonging to a different customer.
    async loadStripeCustomerFromMemberInfo(response) {
      // Extract Stripe Customer if it exists
      console.log("DEBUG: Checking for stripe_customers in response...");
      console.log(
        "DEBUG: stripe_customers =",
        response["data"]["stripe_customers"]
      );

      if (
        response["data"]["stripe_customers"] &&
        response["data"]["stripe_customers"].length > 0
      ) {
        console.log(
          "DEBUG: Found stripe_customers array with length:",
          response["data"]["stripe_customers"].length
        );

        // Find primary Stripe customer
        const primaryStripeCustomer = response["data"]["stripe_customers"].find(
          (customer) => customer["primary_stripe_customer__c"] === true
        );

        console.log("DEBUG: Primary Stripe customer:", primaryStripeCustomer);

        if (
          primaryStripeCustomer &&
          primaryStripeCustomer["stripe_customer_id__c"]
        ) {
          const newCustomerId = primaryStripeCustomer["stripe_customer_id__c"];
          const customerChanged =
            this.existingStripeCustomerId &&
            this.existingStripeCustomerId !== newCustomerId;

          // CRITICAL: If customer changed or we have payment data from previous customer
          if (customerChanged || this.stripeCustomerId) {
            console.log(
              "🔄 Customer changing or clearing previous payment - clearing Stripe data first"
            );

            // Clear previous payment data that might belong to different customer
            this.clearStripePaymentData();

            // Clear Stripe session in child component
            if (this.$refs.stripePaymentForm) {
              await this.$refs.stripePaymentForm.clearStripeSession();
            }
          }

          this.existingStripeCustomerId = newCustomerId;
          this.existingStripeCustomerSfid = primaryStripeCustomer["sfid"];
          console.log(
            "✅ Found existing Stripe customer:",
            this.existingStripeCustomerId
          );
          console.log(
            "✅ Stripe customer SFID:",
            this.existingStripeCustomerSfid
          );

          // Verify the value is actually set
          this.$nextTick(() => {
            console.log(
              "🔍 After nextTick, existingStripeCustomerId:",
              this.existingStripeCustomerId
            );
            console.log(
              "🔍 StripePaymentForm component exists:",
              !!this.$refs.stripePaymentForm
            );
            if (this.$refs.stripePaymentForm) {
              console.log(
                "🔍 StripePaymentForm existingCustomerId prop:",
                this.$refs.stripePaymentForm.existingCustomerId
              );
            }
          });
        } else {
          this.existingStripeCustomerId = null;
          this.existingStripeCustomerSfid = null;

          // ALSO clear payment data if we have any from a different customer
          if (this.stripeCustomerId) {
            console.log(
              "⚠️ No Stripe customer for this member, clearing previous payment data"
            );
            this.clearStripePaymentData();
            if (this.$refs.stripePaymentForm) {
              await this.$refs.stripePaymentForm.clearStripeSession();
            }
          }

          console.log(
            "⚠️ No primary Stripe customer found (primary_stripe_customer__c not true or missing stripe_customer_id__c)"
          );
        }
      } else {
        this.existingStripeCustomerId = null;
        this.existingStripeCustomerSfid = null;

        // ALSO clear payment data if we have any from a different customer
        if (this.stripeCustomerId) {
          console.log(
            "⚠️ No Stripe customer for this member, clearing previous payment data"
          );
          this.clearStripePaymentData();
          if (this.$refs.stripePaymentForm) {
            await this.$refs.stripePaymentForm.clearStripeSession();
          }
        }

        console.log("⚠️ No stripe_customers data in response");
      }
    },
    // Shared renewal helper: find the primary membership/contact/boat in a
    // member-info payload and remember the membership number.
    // Build one context per membership on the account. An account can hold
    // several, and each carries its own contact, boats and ARB state, so the
    // form renders them independently rather than flattening the account down
    // to a single membership.
    buildMembershipContexts(full_data) {
      const memberships = full_data["memberships"] || [];
      const contacts = full_data["contacts"] || [];
      const boats = full_data["boats"] || [];

      return memberships
        .map((membership) => {
          const contact =
            contacts.find(
              (item) => item["sfid"] === membership["membership_contact__c"]
            ) || null;

          // Scope boats to this membership. Picking a primary boat across the
          // whole account bound a vessel from a different membership whenever
          // an account held more than one.
          const membershipBoats = boats.filter(
            (item) => item["related_membership__c"] === membership["sfid"]
          );
          let boat =
            membershipBoats.find((item) => item["primary_boat__c"]) ||
            membershipBoats[0] ||
            null;

          // Every boat in production carries related_membership__c, but if one
          // ever does not, fall back to the account's primary boat - and only
          // when this account has a single membership, where there is no
          // ambiguity about which membership the boat belongs to.
          if (!boat && memberships.length === 1) {
            boat =
              boats.find((item) => item["primary_boat__c"]) || boats[0] || null;
          }

          return {
            membership,
            contact,
            boat,
            boats: membershipBoats,
            isPrimary: !!membership["primary_membership__c"],
            expanded: false,
            saving: false,
          };
        })
        .sort((a, b) => {
          // Primary first, then the latest expiration.
          if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
          const aExp = a.membership["membership_expiration_date__c"] || "";
          const bExp = b.membership["membership_expiration_date__c"] || "";
          return bExp.localeCompare(aExp);
        });
    },

    // Kept for the existing renewal path: resolve the records the main form
    // binds to. Delegates to the context builder so boat scoping is identical.
    loadPrimaryRecords(full_data, membershipSfid) {
      const contexts = this.buildMembershipContexts(full_data);
      this.membershipContexts = contexts;
      this.accountContacts = full_data["contacts"] || [];
      // Kept so selecting another membership later can rebind the form without
      // refetching the account.
      this.full_data = full_data;

      let index = 0;
      if (membershipSfid) {
        const found = contexts.findIndex(
          (context) => context.membership["sfid"] === membershipSfid
        );
        if (found !== -1) index = found;
      }

      const context = contexts[index];
      if (!context) {
        // No membership at all: let the caller fail the same way it used to
        // rather than silently binding an empty form to a real account.
        return {
          primary_membership: undefined,
          primary_contact: undefined,
          primary_boat: undefined,
        };
      }

      this.selectedMembershipIndex = index;
      context.expanded = true;
      this.memno = context.membership["membership_number__c"];

      return {
        primary_membership: context.membership,
        primary_contact: context.contact,
        primary_boat: context.boat,
      };
    },
    // Shared renewal helper: set CardSelection from the membership type and
    // load expiration/status, trial-eligibility and trailer-care fields.
    applyMembershipSelections(primary_membership) {
      //Fill Membership Type at Top
      if (primary_membership["membership_type__c"] != null) {
        console.log(
          primary_membership["membership_type__c"] ==
            "Professional Mariner Card"
        );

        // Set flag to prevent trial validation during programmatic load
        this.isLoadingMembership = true;

        if (
          primary_membership["membership_type__c"] ==
          "Professional Mariner Card"
        ) {
          this.CardSelection = "ProfMariner";
        } else if (primary_membership["membership_type__c"].includes("Trial")) {
          // Trial memberships are Gold Card trials
          console.log(
            "Trial membership detected - setting CardSelection to Gold"
          );
          this.CardSelection = "Gold";
        } else {
          this.CardSelection =
            primary_membership["membership_type__c"].split(" ")[0];
        }

        // Reset flag after setting CardSelection
        this.$nextTick(() => {
          this.isLoadingMembership = false;
        });
      }

      this.membership_expiration_date__c =
        primary_membership["membership_expiration_date__c"];

      this.membership_status__c = primary_membership["membership_status__c"];

      // Load trial eligibility fields
      this.memberships.trial_issued_date__c =
        primary_membership["trial_issued_date__c"];
      this.memberships.next_trial_eligible_in_days__c =
        primary_membership["next_trial_eligible_in_days__c"];

      console.log("Loaded membership trial fields:");
      console.log(
        "  trial_issued_date__c:",
        this.memberships.trial_issued_date__c
      );
      console.log(
        "  next_trial_eligible_in_days__c:",
        this.memberships.next_trial_eligible_in_days__c
      );

      if (primary_membership["trailer_care_type__c"] != null) {
        if (primary_membership["trailer_care_type__c"] == "Trailer Care") {
          this.trailer_care_type__c = "Marine";
          this.TrailerSelection = "Marine";
        } else {
          this.trailer_care_type__c = "Universal";
          this.TrailerSelection = "Universal";
        }
      }
    },
    // Shared renewal helper: copy the primary records into the form state,
    // pick up any ARB on file, and flag the form as a renewal.
    populateRenewalForm(
      full_data,
      { primary_membership, primary_contact, primary_boat }
    ) {
      //Set price so cart price populates properly
      this.updateCartPrice(this.TrailerSelection);
      this.updateCartPrice(this.CardSelection);

      var contact_parsed_obj = JSON.parse(JSON.stringify(this.contacts));
      var contact_keynames = Object.keys(contact_parsed_obj);

      var boat_parsed_obj = JSON.parse(JSON.stringify(this.boats));
      var boat_keynames = Object.keys(boat_parsed_obj);

      var account_parsed_obj = JSON.parse(JSON.stringify(this.account));
      var account_keynames = Object.keys(account_parsed_obj);

      var membership_parsed_obj = JSON.parse(JSON.stringify(this.memberships));
      var membership_keynames = Object.keys(membership_parsed_obj);

      var arbs_parsed_obj = JSON.parse(JSON.stringify(this.arbs));
      var arbs_keynames = Object.keys(arbs_parsed_obj);

      // Scoping records to a single membership means either can legitimately
      // be absent; binding undefined would throw where it used to be
      // guaranteed by picking account-wide.
      if (!primary_contact || !primary_boat) {
        this.$bvToast.toast(
          "This membership is missing its contact or boat record, so it cannot be renewed here. Please check it in Salesforce.",
          {
            title: "Incomplete membership",
            variant: "warning",
            autoHideDelay: 8000,
          }
        );
        return;
      }

      this.contact_sfid = primary_contact["sfid"];
      this.routes.contact = this.contact_sfid;
      contact_keynames.forEach((name) => {
        primary_contact[name] = coerceContactField(name, primary_contact[name]);
        this.contacts[name] = primary_contact[name];
      });

      // Remember the numbers this record arrived with, so validation can tell
      // one the dealer typed from one the member has had on file for years.
      this.rememberLoadedPhones();

      console.log("account");
      this.account_sfid = full_data["account"][0]["sfid"];
      this.routes.account = this.account_sfid;
      account_keynames.forEach((name) => {
        this.account[name] = coerceAccountField(
          name,
          full_data["account"][0][name]
        );
      });

      this.boat_sfid = primary_boat["sfid"];
      this.routes.boat = this.boat_sfid;
      boat_keynames.forEach((name) => {
        if (name == "aor__c") {
          console.log("old aor inside of renew", primary_boat[name]);
          this.old_aor = primary_boat[name];
          console.log("this.old_aor = ", this.old_aor);
        }
        this.boats[name] = coerceBoatField(name, primary_boat[name]);
      });

      this.membership_sfid = primary_membership["sfid"];
      this.routes.membership = this.membership_sfid;
      membership_keynames.forEach((name) => {
        if (
          name == "card_expiration_date__c" ||
          name == "card_number__c" ||
          name == "card_security_code__c" ||
          name == "card_type__c"
        ) {
          console.log("Skipping credit card field " + name);
        } else {
          this.memberships[name] = primary_membership[name];
        }
      });

      if (full_data["arbs"]["Error"] !== undefined) {
        if (
          full_data["arbs"]["Error"] ==
          "No pymt__payment_profile__c found with specified ID"
        ) {
          //no ARB found, nothing to do with it
          console.log("no arb found for user");
        } else {
          //ARB found, populate data so if they renew the member, they can cancel it
          this.arbs_sfid = full_data["arbs"][0]["sfid"];
          this.routes.arbs = this.arbs_sfid;
          arbs_keynames.forEach((name) => {
            //save all arb data in session
            this.arbs[name] = full_data["arbs"][0][name];
          });
        }
      } else {
        console.log("no arb found");
      }

      if (this.account.shippingstreet == this.account.billingstreet) {
        this.shipping_same_as_billing = true;
      } else {
        this.shipping_same_as_billing = false;
      }
      this.isRenew = true;
      //try to assign aor
      this.assignAOR();
      this.$bvToast.toast(
        "The form has been populated with the member's information.",
        {
          title: "Data populated.",
          variant: "success",
          autoHideDelay: 5000,
        }
      );
    },
  },
};
